/**
 * Media kits de programas de la grilla (programGridByNiche) que no tenían propuesta estática.
 * Se fusionan en STATIC_PROPOSALS; landings vía LandingMediaKitProgramStatic + ProgramProposalLayout.
 */

const FILES = 'https://files.tnonetwork.com';
const showLogo = (slug) => `${FILES}/shows/logo/${slug}.png`;
const showUrl = (slug) => `https://tnonetwork.com/show/${slug}`;
const profilePhoto = (talentSlug) => `${FILES}/users/profile/${talentSlug}.jpg`;

function buildProgramProposal({
  slug,
  name,
  headline,
  description,
  sectionBody,
  anchorName = '',
  anchorSubtitle = '',
  anchorBio = '',
  anchorTalentSlug = '',
  order = 1.2,
  videoUrl = '',
}) {
  const logoUrl = showLogo(slug);
  const metaDescription =
    `Anuncia en ${name} en TNO Network. ${description.replace(/\s+/g, ' ').trim().slice(0, 140)}…`;

  return {
    type: 'program',
    slug,
    name,
    metaTitle: `${name} | Anuncia con nosotros`,
    metaDescription,
    content: {
      who: {
        title: headline,
        description,
        sectionTitle: `¿Qué es ${name}?`,
        sectionBody,
        sectionLinkUrl: showUrl(slug),
        sectionLinkLabel: `tnonetwork.com/show/${slug}`,
        logoUrl,
        ...(anchorName
          ? {
            anchorName,
            anchorSubtitle: anchorSubtitle || `Voz de ${name} en TNO Network.`,
            anchorBio:
              anchorBio ||
              `${anchorName} conduce ${name}: conversación con credibilidad, comunidad activa y presencia en vivo y On-Demand.`,
            ...(anchorTalentSlug ? { anchorImageUrl: profilePhoto(anchorTalentSlug) } : {}),
          }
          : {}),
      },
      videoUrl,
      benefitsSection: {},
      benefits: [],
      metrics: [],
      values: {},
      pricingOptions: [],
      areYouReady: {
        title: '¿Listo para patrocinar?',
        description: `Forma parte de ${name} y conecta tu marca con la audiencia de TNO Network.`,
        ctaText: `Patrocinar ${name}`,
        footerText: `© TNO Network 2026 | ${name}.`,
      },
    },
    isActive: true,
    order,
    ogImage: logoUrl,
  };
}

const GRID_PROGRAM_DEFS = [
  {
    slug: 'comerciocorporativo',
    name: 'Comercio Corporativo',
    headline: 'Comercio Corporativo: economía, gestión y éxito empresarial.',
    description:
      'El espacio de TNO Network para coyuntura económica, liderazgo y casos de éxito con rigor y cercanía.',
    sectionBody:
      'Programa de entrevistas con Darío Ascanio: líderes, gestión y decisiones que mueven el sector empresarial. En vivo y On-Demand.',
    anchorName: 'Darío Ascanio',
    anchorTalentSlug: 'darioascanio',
    order: 1.12,
  },
  {
    slug: 'ensintoniacontusalud',
    name: 'En Sintonía con tu Salud',
    headline: 'En Sintonía con tu Salud: bienestar integral y prevención.',
    description:
      'Información clara sobre salud física, mental y hábitos preventivos para una audiencia que prioriza el autocuidado.',
    sectionBody:
      'Con la Dra. Grecia Andreina en TNO Network: entrevistas, orientación y comunidad en vivo y On-Demand.',
    anchorName: 'Dra. Grecia Andreina',
    anchorTalentSlug: 'greciagonzalez',
    order: 1.14,
  },
  {
    slug: 'frecuenciamaterna',
    name: 'Frecuencia Materna',
    headline: 'Frecuencia Materna: maternidad holística desde la gestación.',
    description:
      'Acompañamiento emocional y práctico para madres y familias en cada etapa del camino maternal.',
    sectionBody:
      'Encuentro en antena sobre gestación, parto, lactancia y bienestar familiar en TNO Network.',
    order: 1.16,
  },
  {
    slug: 'madressinfiltro',
    name: 'Madres sin Filtro',
    headline: 'Madres sin Filtro: maternidad real, sin tabúes.',
    description:
      'Motivación, honestidad y comunidad para madres que buscan conversación auténtica y apoyo.',
    sectionBody:
      'Formato de conversación en TNO Network: temas reales de crianza, salud y equilibrio personal.',
    order: 1.18,
  },
  {
    slug: 'aunavoz',
    name: 'A una Voz',
    headline: 'A una Voz: fe, reflexión y comunidad.',
    description:
      'Espacio de conversación espiritual y encuentro para quienes buscan propósito y valores compartidos.',
    sectionBody:
      'Programa del nicho «Lo divino y lo profano» en vivo y On-Demand por TNO Network.',
    order: 1.2,
  },
  {
    slug: 'despuesdelevento',
    name: 'Después del evento',
    headline: 'Después del evento: historias humanas que permanecen.',
    description:
      'Podcast y relatos en TNO Network: vidas reales, aprendizajes y conversación con profundidad.',
    sectionBody:
      'Con Angélica Lugo y Yelitza Yánez: periodismo de soluciones y resiliencia. Martes 7:00 PM en vivo y On-Demand.',
    anchorName: 'Angélica Lugo y Yelitza Yánez',
    order: 1.22,
  },
  {
    slug: 'almahouseradio',
    name: 'Alma House Radio',
    headline: 'Alma House Radio: comunidad y lifestyle residencial.',
    description:
      'Voces del proyecto residencial en antena: convivencia, hogar y experiencias de comunidad.',
    sectionBody:
      'Programa del ecosistema inmobiliario y lifestyle en TNO Network.',
    order: 1.24,
  },
  {
    slug: 'siempremasmusica',
    name: 'Siempre más Música',
    headline: 'Siempre más Música: música, cultura y entretenimiento en antena.',
    description:
      'La vitrina musical de TNO Network para artistas, tendencias y conversación con la audiencia.',
    sectionBody:
      'Con Arlene Flores: entrevistas, lanzamientos y cobertura cultural. Viernes 5:00 PM en vivo y On-Demand.',
    anchorName: 'Arlene Flores',
    anchorTalentSlug: 'arleflores',
    order: 1.26,
  },
  {
    slug: 'showtime',
    name: 'Show Time',
    headline: 'Show Time: revista de arte, espectáculos y entretenimiento.',
    description:
      'Actualidad cultural, estrenos y conversación con voces del mundo del espectáculo.',
    sectionBody:
      'Revista en antena de TNO Network con presencia multiplataforma.',
    order: 1.28,
  },
  {
    slug: 'entrechicastmf',
    name: 'Entre Chicas TMF',
    headline: 'Entre Chicas TMF: moda, estilo y conversación en antena.',
    description:
      'Comunidad femenina activa: tendencias, lifestyle y marcas que dialogan con la audiencia.',
    sectionBody:
      'Programa de moda y estilo en TNO Network.',
    order: 1.3,
  },
  {
    slug: 'enelproscenio',
    name: 'En el Proscenio',
    headline: 'En el Proscenio: teatro, cultura y espectáculos.',
    description:
      'Escena artística venezolana y regional con entrevistas, crítica y promoción cultural.',
    sectionBody:
      'Espacio dedicado al teatro y las artes escénicas en TNO Network.',
    order: 1.32,
  },
  {
    slug: 'nitantunitanyo',
    name: 'Ni tan tu ni tan yo',
    headline: 'Ni tan tu ni tan yo: entretenimiento y conversación en antena.',
    description:
      'Formato ligero y cercano para audiencias que consumen contenido conversacional y viral.',
    sectionBody:
      'Entretenimiento en TNO Network con clips para redes y On-Demand.',
    order: 1.34,
  },
  {
    slug: 'masalladelatendencia',
    name: 'Más allá de la tendencia',
    headline: 'Más allá de la tendencia: actualidad, cultura pop y conversación.',
    description:
      'Análisis de tendencias, entretenimiento y lo que mueve la conversación digital.',
    sectionBody:
      'Programa de actualidad y entretenimiento en la parrilla TNO.',
    order: 1.36,
  },
  {
    slug: '2doescalon',
    name: '2do Escalón',
    headline: '2do Escalón: cultura pop, juventud y entretenimiento.',
    description:
      'Podcast juvenil para marcas que buscan cercanía con universitarios y audiencias digitales nativas.',
    sectionBody:
      'Martes y viernes en vivo; On-Demand y YouTube por TNO Network.',
    order: 1.38,
    videoUrl: 'https://www.youtube.com/embed/uZeFbL-Aiu4',
  },
  {
    slug: 'hablemosdetodo',
    name: 'Hablemos de Todo',
    headline: 'Hablemos de Todo: conversaciones para crecer en lo personal y profesional.',
    description:
      'Talk show para marcas que buscan cercanía con audiencias interesadas en bienestar, liderazgo y desarrollo personal.',
    sectionBody:
      'Con Janeth Cano en TNO Network: entrevistas, reflexión y contenido On-Demand.',
    anchorName: 'Janeth Cano',
    order: 1.39,
    videoUrl: 'https://www.youtube.com/embed/DNYNvZsZMLM',
  },
  {
    slug: 'enterateconflavia',
    name: 'Entérate con Flavia',
    headline: 'Entérate con Flavia — Pasaporte Social: turismo y experiencias.',
    description:
      'Destinos, gastronomía y conversación social con Flavia Sánchez en TNO Network.',
    sectionBody:
      'Cobertura de turismo, rutas y lifestyle con presencia en antena y redes.',
    anchorName: 'Flavia Sánchez',
    anchorTalentSlug: 'flaviasanchez',
    order: 1.4,
  },
  {
    slug: 'estoestasabrosito',
    name: 'Esto está sabrosito',
    headline: 'Esto está sabrosito: gastronomía y experiencias culinarias.',
    description:
      'Sabores, bebidas y cultura gastronómica para marcas del sector food & beverage.',
    sectionBody:
      'Programa del nicho «Del fogón al micrófono» en TNO Network.',
    order: 1.42,
  },
  {
    slug: 'pescandolamoda',
    name: 'Pescando la Moda',
    headline: 'Pescando la Moda: tendencias, estilo y fashion en antena.',
    description:
      'Moda, diseño y conversación fashion para marcas de retail y lifestyle.',
    sectionBody:
      'Programa del nicho «Pasarela al aire» en TNO Network.',
    order: 1.44,
  },
  {
    slug: 'legalmenteenfamilia',
    name: 'Legalmente en Familia',
    headline: 'Legalmente en Familia: alfabetización legal para la familia.',
    description:
      'Orientación jurídica clara con Leila Davogustto Luna: patrimonio, familia y decisiones informadas.',
    sectionBody:
      'Programa de entrevistas legales en vivo y On-Demand. Planes Oro, Plata y Bronce en TNO Network.',
    anchorName: 'Leila Davogustto Luna',
    anchorTalentSlug: 'leiladavogustto',
    order: 1.46,
  },
  {
    slug: 'enproduccionconmem',
    name: 'En Producción con MEM',
    headline: 'En Producción con MEM: cultura publicitaria y marcas.',
    description:
      'María Eugenia Moreno conecta voceros, casos y tendencias del mundo creativo y los medios.',
    sectionBody:
      'Media kit oficial: streaming en vivo, On-Demand, TNO Digest y redes @enproduccionconmem.',
    anchorName: 'María Eugenia Moreno',
    anchorTalentSlug: 'mariaeugeniamoreno',
    order: 1.48,
  },
];

export const PROGRAM_GRID_MEDIA_KIT_PROPOSALS = GRID_PROGRAM_DEFS.map(buildProgramProposal);

/** Programas con landing /:slug vía ProgramProposalLayout (sin componente dedicado propio). */
export const PROGRAM_PROPOSAL_ROUTE_SLUGS = [
  'frecuenciamaterna',
  'madressinfiltro',
  'aunavoz',
  'almahouseradio',
  'showtime',
  'entrechicastmf',
  'enelproscenio',
  'nitantunitanyo',
  'masalladelatendencia',
  'enterateconflavia',
  'estoestasabrosito',
  'pescandolamoda',
];
